import { useRef, useCallback, useEffect } from 'react';
import { setCollections, useCollectionsStore } from '../../store/collectionsStore';
import { collectionService } from '../../services/collectionService';
import { workspaceService } from '../../services/workspaceService';
import { isCloudRestWorkspace } from '../../utils/workspaceStorage';
import { hydrateCollectionsFromApi, dedupeCollectionFolderIds } from '../../utils/collectionFolders';
import { buildTreeLayoutPayload } from '../../utils/sidebarLayout';
import { enrichCloudCollectionsWithLinks } from '../../services/localCollectionLink';

/** Debounced saving of the collection tree. */
export function useTreeSave({
  toastT,
  currentWorkspace,
  isAuthenticated,
  deviceLocalCollections,
}) {
  // ── Batched sidebar-tree persistence (cloud) ───────────────────────────────
  // A single debounced PUT /workspaces/:id/sidebar-layout persists the whole tree
  // (collection order + folder nesting/order + each request's folder_id/sort_order).
  // Local workspaces persist automatically via the collections → snapshot effect.
  const treeSaveTimeoutRef = useRef(null);
  const pendingTreeSaveRef = useRef(null);

  const flushTreeSave = useCallback(async () => {
    if (treeSaveTimeoutRef.current) {
      clearTimeout(treeSaveTimeoutRef.current);
      treeSaveTimeoutRef.current = null;
    }
    const pending = pendingTreeSaveRef.current;
    pendingTreeSaveRef.current = null;
    if (!pending) return;
    if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated) || !currentWorkspace?.id) return;
    try {
      await workspaceService.updateTreeLayout(
        currentWorkspace.id,
        buildTreeLayoutPayload(pending)
      );
    } catch (error) {
      console.error('Failed to persist sidebar tree:', error);
      toastT('toast.saveOrderFailed', 'error', { message: error.message });
      try {
        const workspaceCollections = await collectionService.getCollections(currentWorkspace.id);
        setCollections(
          enrichCloudCollectionsWithLinks(
            hydrateCollectionsFromApi(workspaceCollections),
            deviceLocalCollections
          )
        );
      } catch (reloadError) {
        console.error('Failed to reload collections after tree layout error:', reloadError);
      }
    }
  }, [currentWorkspace, isAuthenticated, toastT, deviceLocalCollections]);

  const scheduleTreeSave = useCallback(
    (nextCollections) => {
      if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated) || !currentWorkspace?.id) {
        return; // Local workspace — snapshot effect persists collections.
      }
      pendingTreeSaveRef.current = nextCollections;
      if (treeSaveTimeoutRef.current) clearTimeout(treeSaveTimeoutRef.current);
      treeSaveTimeoutRef.current = setTimeout(() => { flushTreeSave(); }, 500);
    },
    [currentWorkspace, isAuthenticated, flushTreeSave]
  );

  // Flush a pending tree save on workspace switch (effect cleanup) and before unload.
  useEffect(() => {
    return () => { flushTreeSave(); };
  }, [flushTreeSave]);

  useEffect(() => {
    const onBeforeUnload = () => { flushTreeSave(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [flushTreeSave]);

  // Heal folder ids that two collections SHARE — what "Duplicate collection"
  // produced before 18.09.2026 (it copied `settings.folders`, ids included). The
  // sidebar assumes a folder id names one folder: drag indicators lit up in both
  // collections, and rename / bulk actions resolved "the first collection that
  // has this id". New duplicates no longer do it; this repairs data that already
  // has it, once, and persists it through the ordinary tree save.
  //
  // A store subscription rather than a `collections` dependency: this hook reads
  // the store imperatively everywhere else precisely so a collections change
  // does not re-render its owner.
  useEffect(() => {
    const repair = (collections) => {
      const repaired = dedupeCollectionFolderIds(collections);
      if (repaired === collections) return;
      setCollections(repaired);
      scheduleTreeSave(repaired);
    };
    repair(useCollectionsStore.getState().collections);
    return useCollectionsStore.subscribe((state, prev) => {
      if (state.collections !== prev.collections) repair(state.collections);
    });
  }, [scheduleTreeSave]);

  return { flushTreeSave, scheduleTreeSave };
}
